import React, { useState, useMemo } from 'react'
import { ChevronDown } from 'lucide-react'
import { designTokens } from '@/utils/design-tokens'

interface StatusOption {
  value: string
  label: string
}

interface StatusSelectProps {
  options: StatusOption[]
  selectedValue: string
  onSelect: (value: string) => void
  disabled?: boolean
  error?: string | null
  placeholder?: string
}

const StatusSelect: React.FC<StatusSelectProps> = ({
  options,
  selectedValue,
  onSelect,
  disabled = false,
  error = null,
  placeholder = "선택하세요"
}) => {
  const [isOpen, setIsOpen] = useState(false)

  // Get display label for selected value
  const displayLabel = useMemo(() => {
    if (!selectedValue) {
      return placeholder
    }
    const selectedOption = options.find(opt => opt.value === selectedValue)
    return selectedOption?.label || placeholder
  }, [selectedValue, options, placeholder])

  const handleSelect = (option: StatusOption) => {
    onSelect(option.value)
    setIsOpen(false)
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onSelect('')
  }

  const handleOpen = () => {
    if (!disabled) {
      setIsOpen(!isOpen)
    }
  }

  return (
    <div className="relative">
      {/* Selection Button */}
      <button
        type="button"
        onClick={handleOpen}
        disabled={disabled}
        className="w-full flex items-center gap-1.5 px-3 py-0 h-10 border-2 rounded cursor-pointer transition-all duration-150 text-left justify-between"
        style={{
          backgroundColor: disabled ? designTokens.colors.gray[0] : designTokens.colors.white,
          borderColor: error ? designTokens.colors.error.DEFAULT : designTokens.colors.gray[1],
          fontFamily: designTokens.typography.fontFamily.sans[0],
          fontSize: designTokens.typography.fontSize['body-medium'][0],
          lineHeight: designTokens.typography.fontSize['body-medium'][1].lineHeight,
          color: designTokens.colors.gray[6]
        }}
      >
        <div className="flex-1 flex justify-between items-center">
          <span>{displayLabel}</span>
          <div className="flex items-center gap-2">
            {selectedValue && selectedValue.trim() !== '' && (
              <span
                onClick={handleClear}
                className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                style={{ fontSize: '14px' }}
              >
                ✕
              </span>
            )}
            <ChevronDown
              className="w-5 h-5"
              style={{ color: designTokens.colors.gray[3] }}
            />
          </div>
        </div>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className="absolute z-50 w-full mt-1 bg-white border-2 rounded shadow-lg"
          style={{
            borderColor: designTokens.colors.gray[1]
          }}
        >
          {/* Dropdown Content */}
          <div className="max-h-48 overflow-y-auto">
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => handleSelect(option)}
                className="w-full px-3 py-2 text-left hover:bg-gray-50 transition-colors"
                style={{
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: '16px',
                  color: designTokens.colors.gray[6],
                  backgroundColor: selectedValue === option.value ? designTokens.colors.gray[0] : 'transparent'
                }}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div
          className="mt-1 text-sm"
          style={{
            color: designTokens.colors.error.DEFAULT,
            fontFamily: designTokens.typography.fontFamily.sans[0]
          }}
        >
          {error}
        </div>
      )}

      {/* Click Outside Handler */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  )
}

export default StatusSelect
